import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { HelpCircle, X } from 'lucide-react';
import { TILE_GUIDE } from '../../data/tileGuide';

const NO_HELP = new Set(['manifest', 'vision', 'book', 'vault', 'oath']);

const Section = ({ label, text, accent, delay }) => (
  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay }}>
    <p className="font-['Outfit'] text-[10px] tracking-[0.3em] uppercase mb-1" style={{ color: accent }}>{label}</p>
    <p className="font-['Outfit'] text-sm text-white/80 leading-relaxed">{text}</p>
  </motion.div>
);

/* Floating "?" in the top-right of every open tile → purpose / how / why sheet. */
export const TileHelp = ({ tileId }) => {
  const [open, setOpen] = useState(false);
  const t = TILE_GUIDE.find((g) => g.id === tileId);
  if (!t || NO_HELP.has(tileId)) return null;
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} aria-label={`About ${t.name}`} data-testid={`tile-help-btn-${tileId}`}
        className="fixed z-[305] w-8 h-8 flex items-center justify-center rounded-full active:scale-90 transition-transform"
        style={{ right: 12, top: 'calc(max(var(--safe-area-inset-top, env(safe-area-inset-top, 0px)), 12px) + 2px)', color: '#FFE9A0', border: `1px solid ${t.accent}`, background: `${t.accent}26`, boxShadow: `0 0 10px ${t.accent}55` }}>
        <HelpCircle size={18} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div className="fixed inset-0 z-[360] flex items-center justify-center px-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(6px)' }} onClick={() => setOpen(false)} data-testid="tile-help-sheet">
            <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }} transition={{ type: 'spring', stiffness: 260, damping: 24 }}
              onClick={(e) => e.stopPropagation()} className="w-full max-w-md rounded-3xl px-6 py-6 space-y-5 overflow-y-auto"
              style={{ maxHeight: 'calc(100dvh - var(--safe-area-inset-top, 0px) - var(--safe-area-inset-bottom, 0px) - 48px)', background: `radial-gradient(ellipse at 50% 0%, ${t.accent}33, #07070D 65%), #07070D`, border: `1px solid ${t.accent}66`, boxShadow: `0 0 40px ${t.accent}33` }}>
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-['Outfit'] text-[10px] tracking-[0.35em] uppercase text-white/40">{t.tag}</p>
                  <h3 className="font-['Cinzel'] text-3xl mt-1" style={{ color: t.accent, textShadow: `0 0 18px ${t.accent}80` }}>{t.name}</h3>
                </div>
                <button type="button" onClick={() => setOpen(false)} aria-label="Close" data-testid="tile-help-close" className="text-white/50 p-1"><X size={20} /></button>
              </div>
              <Section label="What it is" text={t.what} accent={t.accent} delay={0.05} />
              <Section label="How it works" text={t.how} accent={t.accent} delay={0.12} />
              <Section label="Why it matters" text={t.why} accent={t.accent} delay={0.19} />
              <button type="button" onClick={() => setOpen(false)} data-testid="tile-help-got-it"
                className="w-full py-3.5 rounded-full font-['Cinzel'] tracking-[0.3em] text-xs text-black"
                style={{ background: `linear-gradient(135deg, #FFE9A0, ${t.accent})`, boxShadow: `0 0 20px ${t.accent}66` }}>GOT IT</button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default TileHelp;
